import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Badge,
  Button,
  Dialog,
  FormField,
  Input,
  formatVnd,
  useToast,
} from "@chotca/ui";
import { Pencil } from "lucide-react";
import type { AdminSubscriptionPlanUpsertRequest } from "@chotca/config";
import { getSdk } from "@chotca/api-client";
import {
  DataTable,
  type Column,
  type RowAction,
} from "@/components/data-table";

type Plan = {
  id: string;
  code: "FREE" | "PRO" | "BUSINESS";
  name: string;
  priceVnd: number;
  originalPriceVnd: number | null;
  durationDays: number;
  sourcingQuota: number;
  maxConcurrentPostings: number;
  boostQuota: number;
  isActive: boolean;
  sortOrder: number;
};

const PLAN_CODES = ["FREE", "PRO", "BUSINESS"] as const;

export function SubscriptionPlansTab() {
  const qc = useQueryClient();
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin", "subscriptionPlans"],
    queryFn: async () => getSdk().admin.listSubscriptionPlans(),
  });
  const [editing, setEditing] = React.useState<Plan | null>(null);
  const [creating, setCreating] = React.useState(false);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "subscriptionPlans"] });
  }

  const items = (data?.items ?? []) as Plan[];
  const columns: Column<Plan>[] = [
    {
      key: "code",
      header: "Gói",
      cell: (p) => (
        <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-bold uppercase">
          {p.code}
        </span>
      ),
    },
    { key: "name", header: "Tên", cell: (p) => p.name },
    {
      key: "price",
      header: "Giá / kỳ",
      align: "right",
      cell: (p) => (
        <span className="font-mono">
          {p.originalPriceVnd != null && p.originalPriceVnd > p.priceVnd ? (
            <span className="mr-1 text-xs text-slate line-through">
              {formatVnd(p.originalPriceVnd)}
            </span>
          ) : null}
          {formatVnd(p.priceVnd)}
        </span>
      ),
    },
    {
      key: "duration",
      header: "Số ngày",
      align: "right",
      cell: (p) => <span className="text-xs">{p.durationDays || "∞"}</span>,
    },
    {
      key: "sourcing",
      header: "Săn / kỳ",
      align: "right",
      cell: (p) => <span className="font-bold">{p.sourcingQuota}</span>,
    },
    {
      key: "postings",
      header: "Tin cùng lúc",
      align: "right",
      cell: (p) => p.maxConcurrentPostings,
    },
    {
      key: "boost",
      header: "Boost / kỳ",
      align: "right",
      cell: (p) => p.boostQuota,
    },
    {
      key: "active",
      header: "Trạng thái",
      cell: (p) =>
        p.isActive ? (
          <Badge tone="lime">Bật</Badge>
        ) : (
          <Badge tone="smoke">Ẩn</Badge>
        ),
    },
  ];

  const rowActions: RowAction<Plan>[] = [
    {
      key: "edit",
      label: "Sửa gói",
      icon: Pencil,
      onClick: (p) => setEditing(p),
    },
  ];

  return (
    <section className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)}>+ Tạo gói</Button>
      </div>

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(p) => p.id}
        rowActions={rowActions}
        emptyText="Chưa có gói nào, bấm + Tạo gói để bắt đầu."
      />

      <PlanDialog
        open={!!editing || creating}
        initial={editing}
        onClose={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSaved={() => {
          refresh();
          setEditing(null);
          setCreating(false);
        }}
      />
    </section>
  );
}

const planFormSchema = z.object({
  code: z.enum(PLAN_CODES),
  name: z.string().trim().min(1, "Nhập tên gói").max(80),
  priceVnd: z.number({ message: "Nhập giá" }).int().min(0),
  // Struck-through "before" price. 0 (or ≤ price) = no strikethrough → null.
  originalPriceVnd: z.number().int().min(0),
  durationDays: z.number({ message: "Nhập số ngày" }).int().min(0),
  sourcingQuota: z.number({ message: "Nhập lượt săn" }).int().min(0),
  maxConcurrentPostings: z.number({ message: "Nhập số tin" }).int().min(0),
  boostQuota: z.number({ message: "Nhập lượt boost" }).int().min(0),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
});
type PlanFormValues = z.infer<typeof planFormSchema>;

function PlanDialog({
  open,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  initial: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PlanFormValues>({
    resolver: zodResolver(planFormSchema),
    mode: "onTouched",
  });

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        code: initial.code,
        name: initial.name,
        priceVnd: initial.priceVnd,
        originalPriceVnd: initial.originalPriceVnd ?? 0,
        durationDays: initial.durationDays,
        sourcingQuota: initial.sourcingQuota,
        maxConcurrentPostings: initial.maxConcurrentPostings,
        boostQuota: initial.boostQuota,
        isActive: initial.isActive,
        sortOrder: initial.sortOrder,
      });
    } else {
      // Creating a new plan — blank defaults; the admin picks the code.
      reset({
        code: "PRO",
        name: "",
        priceVnd: 0,
        originalPriceVnd: 0,
        durationDays: 30,
        sourcingQuota: 0,
        maxConcurrentPostings: 1,
        boostQuota: 0,
        isActive: true,
        sortOrder: 0,
      });
    }
  }, [open, initial, reset]);

  const save = useMutation({
    mutationFn: (values: AdminSubscriptionPlanUpsertRequest) =>
      getSdk().admin.upsertSubscriptionPlan(values),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã lưu gói" });
      onSaved();
    },
    onError: () => toast({ tone: "error", title: "Lưu thất bại" }),
  });

  const onSubmit = handleSubmit((values) =>
    save.mutate({
      ...values,
      // 0 or ≤ price means "no strikethrough" → send null.
      originalPriceVnd:
        values.originalPriceVnd > values.priceVnd
          ? values.originalPriceVnd
          : null,
    }),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={initial ? `Sửa gói ${initial.code}` : "Gói"}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button onClick={onSubmit} loading={save.isPending}>
            Lưu
          </Button>
        </div>
      }
    >
      <form className="space-y-3" onSubmit={onSubmit} noValidate>
        <FormField label="Mã gói" error={errors.code?.message}>
          <select
            className="h-9 w-full rounded-md border border-line bg-white px-2 text-sm disabled:bg-mist disabled:text-slate"
            disabled={!!initial}
            {...register("code")}
          >
            {PLAN_CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Tên gói" error={errors.name?.message}>
          <Input invalid={!!errors.name} {...register("name")} />
        </FormField>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Giá / kỳ (VND)" error={errors.priceVnd?.message}>
            <Input
              type="number"
              min={0}
              invalid={!!errors.priceVnd}
              {...register("priceVnd", { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Giá gốc gạch (0 = không)"
            error={errors.originalPriceVnd?.message}
          >
            <Input
              type="number"
              min={0}
              invalid={!!errors.originalPriceVnd}
              {...register("originalPriceVnd", { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Số ngày (0 = ∞)"
            error={errors.durationDays?.message}
          >
            <Input
              type="number"
              min={0}
              invalid={!!errors.durationDays}
              {...register("durationDays", { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Lượt săn / kỳ"
            error={errors.sourcingQuota?.message}
          >
            <Input
              type="number"
              min={0}
              invalid={!!errors.sourcingQuota}
              {...register("sourcingQuota", { valueAsNumber: true })}
            />
          </FormField>
          <FormField
            label="Tin đăng cùng lúc"
            error={errors.maxConcurrentPostings?.message}
          >
            <Input
              type="number"
              min={0}
              invalid={!!errors.maxConcurrentPostings}
              {...register("maxConcurrentPostings", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Boost / kỳ" error={errors.boostQuota?.message}>
            <Input
              type="number"
              min={0}
              invalid={!!errors.boostQuota}
              {...register("boostQuota", { valueAsNumber: true })}
            />
          </FormField>
          <FormField label="Thứ tự" error={errors.sortOrder?.message}>
            <Input
              type="number"
              {...register("sortOrder", { valueAsNumber: true })}
            />
          </FormField>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...register("isActive")} />
          Đang bật (hiện trên màn hình mua gói)
        </label>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
